/** @format */

import { findIconDefinition, parse } from '@fortawesome/fontawesome-svg-core';
import {
  faBolt,
  faClipboardCheck,
  faEllipsis,
  faLifeRing,
  faMagnifyingGlassChart,
  faScrewdriverWrench,
} from '@fortawesome/pro-solid-svg-icons';

export const SERVICE_TYPE_INSTALLATION_SLUG = 'installation';

/**
 * Font Awesome icon from an admin class string
 * (`fa-duotone fa-thin fa-solar-panel`, `fa-sun`, `sun`).
 * Duotone-thin icons are registered in `src/index.jsx`.
 */
const faIconFromNameCache = new Map();

export function faIconFromName(name, fallback = faEllipsis) {
  const raw = String(name || '').trim();
  if (!raw) return fallback;
  const cached = faIconFromNameCache.get(raw);
  if (cached) return cached;
  const parsed = parse.icon(raw);
  const icon =
    parsed?.prefix && parsed?.iconName
      ? findIconDefinition(parsed) || fallback
      : fallback;
  faIconFromNameCache.set(raw, icon);
  return icon;
}

const SERVICE_TYPE_SORT_ORDER = [
  'installation',
  'vt',
  'sav',
  'diagnostic',
  'depannage',
  'other',
];

/** Slugs that must win over the installation fallback heuristic. */
const NON_INSTALLATION_SERVICE_TYPE_SLUGS = new Set([
  'vt',
  'sav',
  'diagnostic',
  'depannage',
  'other',
]);

const MUI_COLOR_KEYS = new Set([
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
  'disabled',
  'wow',
  'purple',
  'default',
]);

const ICON_FALLBACK_BY_SLUG = {
  installation: faScrewdriverWrench,
  vt: faClipboardCheck,
  sav: faLifeRing,
  diagnostic: faMagnifyingGlassChart,
  depannage: faBolt,
  other: faEllipsis,
};

export function serviceTypeDisplayLabel(serviceType) {
  return serviceType?.label || serviceType?.name || '—';
}

export function isVtOrSavServiceType(serviceType) {
  const slug = (serviceType?.slug || '').toLowerCase();
  return slug === 'vt' || slug === 'sav';
}

/** MUI palette key from API `color` field. */
export function serviceTypeMuiColor(serviceType) {
  const raw = (serviceType?.color || 'primary').toLowerCase();
  return MUI_COLOR_KEYS.has(raw) ? raw : 'primary';
}

function serviceTypeSlugFromEntity(serviceTypeOrSlug) {
  if (!serviceTypeOrSlug) return 'installation';
  if (typeof serviceTypeOrSlug === 'string') {
    return serviceTypeOrSlug.toLowerCase();
  }
  if (serviceTypeOrSlug.serviceTypeSlug) {
    return String(serviceTypeOrSlug.serviceTypeSlug).toLowerCase();
  }
  if (serviceTypeOrSlug.slug) {
    return String(serviceTypeOrSlug.slug).toLowerCase();
  }
  const label = serviceTypeDisplayLabel(serviceTypeOrSlug).toLowerCase();
  if (label.includes('sav')) return 'sav';
  if (label.includes('visite') || label === 'vt') return 'vt';
  if (label.includes('diagnostic')) return 'diagnostic';
  if (label.includes('dépannage') || label.includes('depannage')) {
    return 'depannage';
  }
  if (label.includes('installation') || label.includes('chantier')) {
    return SERVICE_TYPE_INSTALLATION_SLUG;
  }
  return 'other';
}

export function iconForServiceType(serviceType) {
  const fromApi = (serviceType?.icon || '').trim();
  if (fromApi) return fromApi;

  const slug = (serviceType?.slug || '').toLowerCase();
  if (ICON_FALLBACK_BY_SLUG[slug]) return ICON_FALLBACK_BY_SLUG[slug];

  const label = serviceTypeDisplayLabel(serviceType).toLowerCase();
  if (label.includes('installation') || label.includes('chantier')) {
    return faScrewdriverWrench;
  }
  if (label.includes('visite') || label === 'vt') return faClipboardCheck;
  if (label.includes('sav')) return faLifeRing;
  if (label.includes('diagnostic')) return faMagnifyingGlassChart;
  if (label.includes('dépannage') || label.includes('depannage')) {
    return faBolt;
  }
  return faEllipsis;
}

/** Font Awesome icon definition for a sub-domain (map pins, list badges). */
export function faIconForServiceType(serviceTypeOrSlug) {
  const entity =
    typeof serviceTypeOrSlug === 'string'
      ? { slug: serviceTypeOrSlug }
      : serviceTypeOrSlug;
  const resolved = iconForServiceType(entity);
  if (typeof resolved !== 'string') return resolved;

  const slug = serviceTypeSlugFromEntity(serviceTypeOrSlug);
  return ICON_FALLBACK_BY_SLUG[slug] || ICON_FALLBACK_BY_SLUG.other;
}

/** Layer / slug / catalog row for icon and color helpers in the UI. */
export function resolveServiceTypeForDisplay(source, serviceTypes) {
  if (!source) return { slug: SERVICE_TYPE_INSTALLATION_SLUG };
  if (typeof source === 'string') return { slug: source };
  if (source.serviceTypeId != null && serviceTypes?.length) {
    const match = serviceTypes.find(
      (row) => Number(row.id) === Number(source.serviceTypeId),
    );
    if (match) return match;
  }
  return source;
}

/** Inline SVG glyph for map pins (Font Awesome path, scaled into the pin head). */
export function pinSvgGlyphForServiceType(serviceTypeOrSlug) {
  const slug = serviceTypeSlugFromEntity(serviceTypeOrSlug);
  const iconDef = ICON_FALLBACK_BY_SLUG[slug] || ICON_FALLBACK_BY_SLUG.other;
  const iconMeta = iconDef?.icon;
  if (!iconMeta) return null;

  const pathData = iconMeta[4];
  const paths = Array.isArray(pathData) ? pathData : [pathData];
  const pathEls = paths
    .map((d) => {
      const safe = String(d).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
      return `<path fill="#ffffff" d="${safe}"/>`;
    })
    .join('');

  return `<g transform="translate(12,9.2) scale(0.02) translate(-256,-256)">${pathEls}</g>`;
}

export function serviceTypeTileStyles(serviceType, { selected, theme }) {
  const muiColor = serviceTypeMuiColor(serviceType);
  const { main } = theme.palette[muiColor] || theme.palette.primary;

  return {
    borderColor: selected ? main : 'transparent',
    bgcolor: selected ? '#fff' : '#f0eeea',
    color: main,
    badgeBg: main,
    hoverBorder: selected ? main : 'rgba(0,0,0,0.18)',
    hoverBg: '#fff',
  };
}

export function sortServiceTypes(serviceTypes) {
  return [...(serviceTypes || [])].sort((a, b) => {
    const aSlug = (a.slug || '').toLowerCase();
    const bSlug = (b.slug || '').toLowerCase();
    const aIndex = SERVICE_TYPE_SORT_ORDER.indexOf(aSlug);
    const bIndex = SERVICE_TYPE_SORT_ORDER.indexOf(bSlug);
    const aOrder = aIndex < 0 ? 99 : aIndex;
    const bOrder = bIndex < 0 ? 99 : bIndex;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return serviceTypeDisplayLabel(a).localeCompare(
      serviceTypeDisplayLabel(b),
      'fr',
    );
  });
}

/** Whether a project intervention layer belongs to this sub-domain catalog entry. */
export function layerMatchesServiceType(layer, serviceType) {
  if (!layer || !serviceType) return false;
  if (
    layer.serviceTypeId != null &&
    Number(layer.serviceTypeId) === Number(serviceType.id)
  ) {
    return true;
  }

  const filterSlug = (serviceType.slug || '').toLowerCase();
  const layerSlug = (layer.serviceTypeSlug || '').toLowerCase();
  if (filterSlug && layerSlug && filterSlug === layerSlug) {
    return true;
  }

  if (layer.serviceTypeId != null) return false;

  const slug = filterSlug;
  const layerSubLabel = (layer.serviceTypeLabel || '').toLowerCase();
  const subLabel = serviceTypeDisplayLabel(serviceType).toLowerCase();
  if (layerSubLabel && layerSubLabel === subLabel) return true;

  if (slug === SERVICE_TYPE_INSTALLATION_SLUG) {
    if (layerSlug && NON_INSTALLATION_SERVICE_TYPE_SLUGS.has(layerSlug)) {
      return false;
    }
    if (
      layerSubLabel.includes('sav') ||
      layerSubLabel.includes('visite') ||
      layerSubLabel === 'vt' ||
      layerSubLabel.includes('diagnostic') ||
      layerSubLabel.includes('dépannage') ||
      layerSubLabel.includes('depannage')
    ) {
      return false;
    }
    const domain = (layer.domainLabel || '').toLowerCase();
    const label = (layer.label || '').toLowerCase();
    return (
      domain.includes('chantier') ||
      domain.includes('installation') ||
      label.includes('chantier') ||
      (!layer.serviceTypeLabel &&
        !domain.includes('sav') &&
        !domain.includes('vt'))
    );
  }

  return slug === layerSubLabel.replace(/\s+/g, '-');
}

/** All intervention layers for a project that match this sub-domain. */
export function findLayersForServiceType(layers, serviceType) {
  if (!serviceType) return [];
  const list = layers || [];
  const byId = list.filter(
    (layer) => Number(layer.serviceTypeId) === Number(serviceType.id),
  );
  if (byId.length > 0) return byId;
  return list.filter((layer) => layerMatchesServiceType(layer, serviceType));
}

export function findLayerForServiceType(layers, serviceType) {
  const matches = findLayersForServiceType(layers, serviceType);
  return matches[0] ?? null;
}

export function interventionLayerDefaultLabel(layer) {
  if (!layer) return 'Intervention';
  if (layer.serviceTypeLabel?.trim()) return layer.serviceTypeLabel.trim();
  if (layer.domainLabel?.trim()) return layer.domainLabel.trim();
  return 'Intervention';
}

export function layerDisplayLabel(layer, serviceType = null) {
  if (!layer) return 'Intervention';
  if (layer.label?.trim()) return layer.label.trim();
  if (layer.subject?.trim()) return layer.subject.trim();
  const subLabel = serviceType
    ? serviceTypeDisplayLabel(serviceType)
    : layer.serviceTypeLabel;
  if (layer.domainLabel && layer.domainLabel !== subLabel) {
    return layer.domainLabel;
  }
  return subLabel || 'Intervention';
}

/** Short label for sub-domain chips (trip row, sidebar card, etc.). */
export function serviceTypeChipLabel(entity) {
  if (!entity) return null;
  const fromLabel = (entity.serviceTypeLabel || '').trim();
  if (fromLabel) return fromLabel;
  const slug = String(entity.serviceTypeSlug || entity.domain || '')
    .trim()
    .toLowerCase();
  if (!slug) return null;
  if (slug === 'sav') return 'SAV';
  if (slug === 'vt') return 'VT';
  if (slug === 'installation') return 'Installation';
  return slug.charAt(0).toUpperCase() + slug.slice(1);
}

/** Intervention layer count for the left sub-domain rail badge. */
export function layerCountForServiceType(layers, serviceType) {
  return findLayersForServiceType(layers, serviceType).length;
}

/** @deprecated Use layerCountForServiceType */
export function tripCountForServiceType(layers, _projectTrips, serviceType) {
  return layerCountForServiceType(layers, serviceType);
}

export function serviceTypeIdFromSlug(slug, serviceTypes) {
  const normalized = String(slug || '').toLowerCase();
  if (!normalized) return null;
  const match = (serviceTypes || []).find(
    (row) => (row.slug || '').toLowerCase() === normalized,
  );
  return match?.id ?? null;
}

export function serviceTypeIdForTrip(trip, serviceTypes) {
  if (!trip) return null;
  return serviceTypeIdFromSlug(trip.serviceTypeSlug, serviceTypes);
}

export function serviceTypeIdForLayer(layer, serviceTypes) {
  if (!layer) return null;
  if (layer.serviceTypeId != null) return layer.serviceTypeId;
  const fromSlug = serviceTypeIdFromSlug(layer.serviceTypeSlug, serviceTypes);
  if (fromSlug != null) return fromSlug;

  const list = serviceTypes || [];
  const nonInstallation = list.filter(
    (sub) => (sub.slug || '').toLowerCase() !== SERVICE_TYPE_INSTALLATION_SLUG,
  );
  const specific = nonInstallation.find((sub) =>
    layerMatchesServiceType(layer, sub),
  );
  if (specific) return specific.id;

  const installation = list.find(
    (sub) => (sub.slug || '').toLowerCase() === SERVICE_TYPE_INSTALLATION_SLUG,
  );
  if (installation && layerMatchesServiceType(layer, installation)) {
    return installation.id;
  }
  return null;
}

/** Map grid / modal payload to ProjectPopup selection state (after project load). */
export function resolveInitialTripSelection({
  layers,
  serviceTypes,
  trips,
  tripId,
  layerId,
  serviceTypeSlug,
}) {
  const layerList = layers || [];
  const tripList = trips || [];

  const trip =
    tripId != null
      ? tripList.find((row) => Number(row.id) === Number(tripId))
      : null;
  let layer =
    layerId != null
      ? layerList.find((row) => Number(row.id) === Number(layerId))
      : null;

  if (!layer && trip?.interventionId != null) {
    layer = layerList.find(
      (row) => Number(row.id) === Number(trip.interventionId),
    );
  }

  const serviceTypeId =
    serviceTypeIdFromSlug(serviceTypeSlug, serviceTypes) ??
    serviceTypeIdForLayer(layer, serviceTypes) ??
    serviceTypeIdForTrip(trip, serviceTypes);
  let expandedLayerId = null;
  if (layer?.id != null) {
    expandedLayerId = Number(layer.id);
  } else if (layerId != null) {
    expandedLayerId = Number(layerId);
  }
  let selectedTripId = null;
  if (trip?.id != null) {
    selectedTripId = Number(trip.id);
  } else if (tripId != null) {
    selectedTripId = Number(tripId);
  }

  return {
    serviceTypeId,
    expandedLayerId,
    selectedTripId,
    expandedProjectId:
      layer?.clientId != null ? Number(layer.clientId) : null,
  };
}
